import React, { useState, useEffect } from 'react';
import './Activity.css';

interface NetworkData {
  amount: number;
  apr: number;
}

const Activity: React.FC = () => {
  const [stakingData, setStakingData] = useState<{
    totalStaked: number;
    networks: Record<string, NetworkData>;
  }>({
    totalStaked: 0,
    networks: {
      ethereum: { amount: 0, apr: 0 },
      solana: { amount: 0, apr: 0 },
      bnb: { amount: 0, apr: 0 },
    },
  });

  const [withdrawals, setWithdrawals] = useState<string[]>([]);
  const [stakingActivities, setStakingActivities] = useState<string[]>([]);

  useEffect(() => {
    // Only load from localStorage once on mount
    const storedStakingData = localStorage.getItem('stakingData');
    if (storedStakingData) {
      setStakingData(JSON.parse(storedStakingData));
    } else {
      // If no data in localStorage, initialize with default values
      const now = new Date();
      const hour = now.getHours();
      const multiplier = 1 + (hour % 6) * 0.05;

      const generateDeterministicAPR = (network: string, hour: number) => {
        const base = 40;
        const range = 170;
        const seed = hashString(network + hour);
        const normalized = seed % 1000 / 1000;
        return Math.round(base + normalized * range);
      };

      const hashString = (str: string) => {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
          hash = str.charCodeAt(i) + ((hash << 5) - hash);
        }
        return Math.abs(hash);
      };

      const networkBase = {
        ethereum: { amount: 117 * multiplier, apr: generateDeterministicAPR('ethereum', hour) },
        solana: { amount: 2100 * multiplier, apr: generateDeterministicAPR('solana', hour) },
        bnb: { amount: 1400 * multiplier, apr: generateDeterministicAPR('bnb', hour) },
      };

      const totalStaked = Object.entries(networkBase).reduce((sum, [network, data]) => {
        return sum + data.amount;
      }, 0);

      const initialStakingData = {
        totalStaked,
        networks: networkBase,
      };

      setStakingData(initialStakingData);
      localStorage.setItem('stakingData', JSON.stringify(initialStakingData)); // Persist initial data to localStorage
    }

    // Simulate a random withdrawal activity
    const withdrawalInterval = setInterval(() => {
      const randomNetwork = ['solana', 'ethereum', 'bnb'][Math.floor(Math.random() * 3)];
      const amount = (Math.random() * 9 + 1);
      const wallet = getWalletAddress(randomNetwork);

      setWithdrawals((prevWithdrawals) => [
        ...prevWithdrawals,
        `${wallet} withdrew ${amount.toFixed(2)} ${getSymbol(randomNetwork)}.`,
      ]);
    }, Math.floor(Math.random() * (120000 - 10000) + 10000)); // Random interval between 10 and 120 seconds

    // Simulate random staking activity
    const stakingInterval = setInterval(() => {
      const randomNetwork = ['solana', 'ethereum', 'bnb'][Math.floor(Math.random() * 3)];
      const amount = Math.floor(Math.random() * 50 + 1);
      const wallet = getWalletAddress(randomNetwork);

      // Update staking data to include the new staking activity
      setStakingData((prevState) => {
        const newAmount = prevState.networks[randomNetwork].amount + amount;
        const updatedNetworks = {
          ...prevState.networks,
          [randomNetwork]: { ...prevState.networks[randomNetwork], amount: newAmount },
        };

        const updatedTotalStaked = Object.entries(updatedNetworks).reduce((sum, [network, data]) => {
          return sum + data.amount;
        }, 0);

        const newStakingData = {
          totalStaked: updatedTotalStaked,
          networks: updatedNetworks,
        };

        // Persist the updated staking data to localStorage
        localStorage.setItem('stakingData', JSON.stringify(newStakingData));

        return newStakingData;
      });

      setStakingActivities((prevStakingActivities) => [
        ...prevStakingActivities,
        `${wallet} staked ${amount} ${getSymbol(randomNetwork)}.`,
      ]);
    }, Math.floor(Math.random() * (120000 - 10000) + 10000)); // Random interval between 10 and 120 seconds

    return () => {
      clearInterval(withdrawalInterval); // Cleanup on component unmount
      clearInterval(stakingInterval); // Cleanup on component unmount
    };
  }, []);

  const getWalletAddress = (network: string): string => {
    if (network === 'solana') {
      return `S${Math.random().toString(36).substr(2, 32)}`.slice(0, 6) + '.....' + Math.random().toString(36).substr(2, 4);
    }
    return `0x${Math.random().toString(16).substr(2, 40)}`.slice(0, 6) + '.....' + Math.random().toString(16).substr(2, 4);
  };

  return (
    <div>
      <div className="window-title">LIVE Activity</div>
      {/* Logo at the very top */}
      <div className="logo-container">
        <img src="/logo.png" alt="AI Crypto Assistant Logo" className="logo-image" />
      </div>
      <div className="window-content">
        <div className="activity-wrapper">
          <div className="activity-window">
            <div className="window-body">
              {/* Staking Information Section */}
              <div className="activity-section staking-info">
                <h3>Staking Information</h3>
                {Object.entries(stakingData.networks).map(([network, data]) => (
                  <div className="network-item" key={network}>
                    <strong>{capitalize(network)}:</strong> {formatNumber(data.amount)} {getSymbol(network)}
                    {' '} | <span className="apr">APR: {data.apr}%</span>
                  </div>
                ))}
              </div>

              {/* Withdraw Activity Section */}
              <div className="activity-section withdrawal-activity">
                <h3>Withdrawal Activity</h3>
                <div className="activity-list" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {withdrawals.length > 0 ? (
                    withdrawals.map((withdrawal, index) => (
                      <div key={index} className="withdrawal-item">
                        {withdrawal}
                      </div>
                    ))
                  ) : (
                    <div>No withdrawals yet.</div>
                  )}
                </div>
              </div>

              {/* Staking Activity Section */}
              <div className="activity-section staking-activity">
                <h3>Staking Activity</h3>
                <div className="activity-list" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {stakingActivities.length > 0 ? (
                    stakingActivities.map((activity, index) => (
                      <div key={index} className="staking-activity-item">
                        {activity}
                      </div>
                    ))
                  ) : (
                    <div>No staking yet.</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper functions
const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);
const getSymbol = (network: string) =>
  network === 'solana' ? 'SOL' : network === 'bnb' ? 'BNB' : 'ETH';
const formatNumber = (num: number) =>
  num >= 1000 ? num.toLocaleString(undefined, { maximumFractionDigits: 2 }) : num.toFixed(2);

export default Activity;
